import { ref, computed } from 'vue'

/**
 * Shared PWA-install state.
 *
 * The browser fires `beforeinstallprompt` once, early on app load — usually
 * before the user reaches any particular screen. So we capture it GLOBALLY
 * (wirePwaInstall, called from main.js) instead of inside one view, where the
 * event would be missed if that view isn't mounted yet. Chrome also requires a
 * user gesture to actually show the native install dialog, so we stash the
 * deferred event and trigger it from our own banner/button on tap.
 */
const deferredPrompt = ref(null)
const installed = ref(false)
const dismissedUntil = ref(Number(localStorage.getItem('pwa_install_dismissed_until') || 0))
let wired = false

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
}

export function wirePwaInstall() {
  if (wired) return
  wired = true
  if (isStandalone()) installed.value = true
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()         // suppress Chrome's default mini-infobar
    deferredPrompt.value = e   // keep it to fire from our own UI (needs a gesture)
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferredPrompt.value = null
  })
}

export function usePwaInstall() {
  const canInstall = computed(() => !!deferredPrompt.value && !installed.value)
  // The auto banner shows only when installable, not already installed, and not
  // snoozed (the user tapped "later" within the snooze window).
  const showBanner = computed(() => canInstall.value && Date.now() >= dismissedUntil.value)

  async function promptInstall() {
    const p = deferredPrompt.value
    if (!p) return 'unavailable'
    p.prompt()
    const { outcome } = await p.userChoice
    deferredPrompt.value = null   // a deferred prompt is single-use
    if (outcome === 'accepted') installed.value = true
    return outcome
  }

  function snooze(days = 7) {
    const until = Date.now() + days * 86400000
    localStorage.setItem('pwa_install_dismissed_until', String(until))
    dismissedUntil.value = until
  }

  return { canInstall, showBanner, installed, promptInstall, snooze }
}
